import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useUiStore, toDateStr } from '../../lib/uiStore';
import { useLang } from '../../lib/lang';
import type { Session } from '../../lib/types';

export function MonthView({ branchId, date }: { branchId: number; date: string }): JSX.Element {
  const setDate = useUiStore((s) => s.setDate);
  const setView = useUiStore((s) => s.setView);
  const { t, lang } = useLang();
  const locale = lang === 'ar' ? 'ar' : 'en-US';

  const monthDate = new Date(`${date}T00:00:00`);
  const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const rangeFrom = new Date(firstOfMonth); rangeFrom.setDate(rangeFrom.getDate() - 7);
  const rangeTo = new Date(firstOfMonth); rangeTo.setMonth(rangeTo.getMonth() + 1); rangeTo.setDate(rangeTo.getDate() + 7);

  const monthKey = `${monthDate.getFullYear()}-${monthDate.getMonth()}`;
  const q = useQuery({
    queryKey: ['calendar-month', branchId, monthKey],
    queryFn: () => api.calendar(branchId, { from: rangeFrom.toISOString(), to: rangeTo.toISOString() }),
  });

  const fdow = q.data?.settings.first_day_of_week ?? 6;

  const byDay = useMemo(() => {
    const map = new Map<string, Session[]>();
    for (const s of q.data?.sessions ?? []) {
      const key = toDateStr(new Date(s.start_at));
      const arr = map.get(key) ?? [];
      arr.push(s);
      map.set(key, arr);
    }
    return map;
  }, [q.data]);

  const cells = useMemo(() => {
    const gridStart = new Date(firstOfMonth);
    const diff = (gridStart.getDay() - fdow + 7) % 7;
    gridStart.setDate(gridStart.getDate() - diff);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(d.getDate() + i);
      return d;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstOfMonth.getTime(), fdow]);

  // Header weekday names derived from the first row of cells (already ordered by fdow).
  const headers = cells.slice(0, 7).map((d) => new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d));
  const todayStr = toDateStr(new Date());
  const thisMonth = monthDate.getMonth();

  const open = (d: Date): void => { setDate(toDateStr(d)); setView('day'); };

  if (q.isLoading) return <div className="flex h-full items-center justify-center text-sm text-slate-400">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>;

  /*
   * A month is 7 columns wide at every size — that is what makes it a month —
   * so the responsive question is only what fits inside a cell. On a phone a
   * cell is about 46px across, where a truncated customer name is noise; it
   * shows coloured dots instead, one per appointment, which still answers "how
   * busy is that day and with what". Named chips return from `sm`.
   *
   * The grid keeps a minimum height and the container scrolls, rather than six
   * rows being crushed into whatever is left below the header.
   */
  return (
    <div dir="ltr" className="flex h-full flex-col p-2 sm:p-3">
      <div className="grid grid-cols-7 border-b border-slate-200 pb-1 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:text-xs dark:border-slate-800">
        {headers.map((h, i) => <div key={i} className="truncate">{h}</div>)}
      </div>
      <div className="appt-scroll min-h-0 flex-1 overflow-y-auto">
        <div className="grid h-full min-h-[420px] grid-cols-7 grid-rows-6 gap-px bg-slate-200 sm:min-h-[520px] dark:bg-slate-800">
          {cells.map((d) => {
            const ds = toDateStr(d);
            const items = byDay.get(ds) ?? [];
            const isToday = ds === todayStr;
            const dim = d.getMonth() !== thisMonth;
            return (
              <button
                key={ds}
                onClick={() => open(d)}
                className={`flex flex-col gap-0.5 overflow-hidden p-1 text-start transition hover:bg-brand/5 sm:p-1.5 ${dim ? 'bg-slate-50 dark:bg-slate-900/60' : 'bg-white dark:bg-slate-900'}`}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className={`text-[11px] font-semibold sm:text-xs ${isToday ? 'flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white' : dim ? 'text-slate-400' : 'text-slate-700 dark:text-slate-200'}`}>
                    {d.getDate()}
                  </span>
                  {items.length > 0 && <span className="text-[10px] font-semibold text-slate-400">{items.length}</span>}
                </div>

                {/* Phone: dots. */}
                <div className="flex flex-wrap gap-0.5 sm:hidden">
                  {items.slice(0, 6).map((s) => (
                    <span key={s.id} className="h-1.5 w-1.5 rounded-full" style={{ background: s.color ?? s.status_color ?? '#3B82F6' }} />
                  ))}
                </div>

                {/* sm and up: named chips. */}
                <div className="hidden flex-col gap-0.5 sm:flex">
                  {items.slice(0, 3).map((s) => (
                    <div key={s.id} className="truncate rounded px-1 text-[10px] font-medium text-white" style={{ background: s.color ?? s.status_color ?? '#3B82F6' }}>
                      {s.customer_name ?? t({ en: 'Walk-in', ar: 'زائر' })}
                    </div>
                  ))}
                  {items.length > 3 && <span className="text-[10px] text-slate-400">+{items.length - 3} {t({ en: 'more', ar: 'المزيد' })}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
